import { create } from "zustand";
import { Track } from "@/types/track";

export type AuthModalReason = "play" | "license" | "playlist";

interface PendingDownload {
  track: Track;
  quality?: "mp3" | "wav";
}

interface PlayerState {
  /** Wipe playback/queue/history/pending state (logout/account switch). Keeps volume/mute prefs. */
  reset: () => void;
  isPlaying: boolean;
  currentTrack: Track | null;
  queue: Track[];
  originalQueue: Track[];
  history: Track[];
  position: number; // 0-100 percentage
  duration: number; // in seconds
  volume: number; // 0-1
  isMuted: boolean;
  isShuffled: boolean;
  repeatMode: "off" | "all" | "one";
  isExpanded: boolean;
  isMinimized: boolean;
  pendingDownload: PendingDownload | null;
  showAuthModal: boolean;
  /** Why the sign-in dialog opened: its title and line say so ("license" from a Request license button). */
  authModalReason: AuthModalReason;
  // Actions
  playTrack: (track: Track, requireAuth?: boolean) => void;
  pauseTrack: () => void;
  resumeTrack: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  setPosition: (position: number) => void;
  /** A seek someone outside the player asked for (0-100); PlayerProvider applies it to the audio. */
  seekRequest: { position: number; at: number } | null;
  requestSeek: (position: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  toggleShuffle: () => void;
  toggleRepeatMode: () => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (trackId: string) => void;
  clearQueue: () => void;
  setDuration: (duration: number) => void;
  toggleExpanded: () => void;
  toggleMinimized: () => void;
  clearTrack: () => void;
  stopCurrentTrack: () => void;
  playFromQueue: (
    tracks: Track[],
    startIndex: number,
    requireAuth?: boolean
  ) => void;
  setQueueContext: (tracks: Track[], startIndex: number) => void;
  setShowAuthModal: (show: boolean, reason?: AuthModalReason) => void;
  setPendingDownload: (pending: PendingDownload | null) => void;
  /** Runs the download asked for behind the sign-in gate; null when none was pending. */
  executePendingDownload: () => Promise<import("./download-track").DownloadResult | null>;
}

const MAX_HISTORY = 200;

export const usePlayerStore = create<PlayerState>((set, get) => ({
  isPlaying: false,
  currentTrack: null,
  queue: [],
  originalQueue: [],
  history: [],
  position: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  isShuffled: false,
  repeatMode: "off",
  isExpanded: false,
  isMinimized: false,
  pendingDownload: null,
  showAuthModal: false,
  authModalReason: "play",
  seekRequest: null,

  reset: () =>
    set({
      isPlaying: false,
      currentTrack: null,
      queue: [],
      originalQueue: [],
      history: [],
      position: 0,
      duration: 0,
      isShuffled: false,
      repeatMode: "off",
      isExpanded: false,
      isMinimized: false,
      pendingDownload: null,
      showAuthModal: false,
      authModalReason: "play",
    }),

  playTrack: (track, requireAuth = true) => {
    const { currentTrack, history } = get();
    if (currentTrack) {
      set({ history: [...history, currentTrack].slice(-MAX_HISTORY) });
    }

    // Immediately stop any current playback
    set({ isPlaying: false });

    set({
      currentTrack: track,
      isPlaying: true, // Start playing immediately; UI will load and attempt playback
      position: 0,
      duration: calculateDuration(track.duration),
      isMinimized: false,
      queue: [], // Clear any existing queue — single track mode
      originalQueue: [],
    });
  },

  pauseTrack: () => set({ isPlaying: false }),

  resumeTrack: () => set({ isPlaying: true }),

  nextTrack: () => {
    const { queue, currentTrack, history, repeatMode } = get();
    if (queue.length === 0) {
      if (repeatMode === "all" && history.length > 0) {
        const nextTrack = history[0];
        set({
          currentTrack: nextTrack,
          history: history.slice(1),
          position: 0,
          duration: calculateDuration(nextTrack.duration),
        });
      } else if (repeatMode === "one" && currentTrack) {
        set({ position: 0 });
      }
      return;
    }
    // Always play the first track in the queue
    const nextTrack = queue[0];
    const newQueue = queue.slice(1);
    if (currentTrack) {
      set({ history: [...history, currentTrack].slice(-MAX_HISTORY) });
    }
    set({
      currentTrack: nextTrack,
      queue: newQueue,
      position: 0,
      duration: calculateDuration(nextTrack.duration),
    });
  },

  previousTrack: () => {
    const { history, currentTrack, position } = get();

    // If current position is more than 3 seconds in, restart the current track
    if (position > 3) {
      set({ position: 0 });
      return;
    }

    if (history.length === 0) return;

    const previousTrack = history[history.length - 1];
    const newHistory = history.slice(0, -1);

    // Add current track to queue if it exists
    if (currentTrack) {
      set({
        queue: [currentTrack, ...get().queue],
      });
    }

    set({
      currentTrack: previousTrack,
      history: newHistory,
      position: 0,
      duration: calculateDuration(previousTrack.duration),
    });
  },

  setPosition: (position) => set({ position }),

  requestSeek: (position) => {
    const clamped = Math.max(0, Math.min(100, position));
    set({ position: clamped, seekRequest: { position: clamped, at: Date.now() } });
  },

  setVolume: (volume) => set({ volume, isMuted: volume === 0 }),

  toggleMute: () => {
    const { isMuted } = get();
    if (isMuted) {
      set({ isMuted: false });
    } else {
      set({ isMuted: true });
    }
  },

  toggleShuffle: () => {
    const { isShuffled, originalQueue } = get();
    if (!isShuffled) {
      // Shuffle queue
      const shuffled = [...originalQueue].sort(() => Math.random() - 0.5);
      set({ queue: shuffled, isShuffled: true });
    } else {
      // Restore queue
      set({ queue: [...originalQueue], isShuffled: false });
    }
  },

  toggleRepeatMode: () => {
    const { repeatMode } = get();
    const modes: ("off" | "all" | "one")[] = ["off", "all", "one"];
    const currentIndex = modes.indexOf(repeatMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    set({ repeatMode: modes[nextIndex] });
  },

  addToQueue: (track) => set({ queue: [...get().queue, track] }),

  removeFromQueue: (trackId) =>
    set({
      queue: get().queue.filter((track) => track.id !== trackId),
    }),

  clearQueue: () => set({ queue: [] }),

  setDuration: (duration) => set({ duration }),

  toggleExpanded: () =>
    set({
      isExpanded: !get().isExpanded,
      isMinimized: false, // Ensure player is not minimized when expanded
    }),

  toggleMinimized: () =>
    set({
      isMinimized: !get().isMinimized,
      isExpanded: false, // Ensure player is not expanded when minimized
    }),

  clearTrack: () => set({ currentTrack: null, isPlaying: false }),

  // Immediately stop current track when switching
  stopCurrentTrack: () => set({ isPlaying: false }),
  playFromQueue: (tracks: Track[], startIndex: number, requireAuth = true) => {
    set({
      originalQueue: tracks.slice(startIndex + 1),
      queue: tracks.slice(startIndex + 1),
      history: tracks.slice(Math.max(0, startIndex - MAX_HISTORY), startIndex),
      currentTrack: tracks[startIndex],
      isPlaying: true,
      position: 0,
      duration: calculateDuration(tracks[startIndex].duration),
      isMinimized: false,
    });
  },
  setShowAuthModal: (show: boolean, reason: AuthModalReason = "play") => set({ showAuthModal: show, authModalReason: reason }),
  setPendingDownload: (pending: PendingDownload | null) =>
    set({ pendingDownload: pending }),
  executePendingDownload: async () => {
    const { pendingDownload } = get();
    if (!pendingDownload) return null;

    // Import dynamically to avoid circular dependencies
    const { downloadTrackResult } = await import("./download-track");
    const result = await downloadTrackResult(
      pendingDownload.track,
      pendingDownload.quality ? { quality: pendingDownload.quality } : undefined
    );

    set({ pendingDownload: null });
    return result;
  },
  setQueueContext: (tracks: Track[], startIndex: number) => {
    set({
      originalQueue: tracks.slice(startIndex + 1),
      queue: tracks.slice(startIndex + 1),
      history: tracks.slice(Math.max(0, startIndex - MAX_HISTORY), startIndex),
      currentTrack: tracks[startIndex],
      isPlaying: true,
      position: 0,
      duration: calculateDuration(tracks[startIndex].duration),
      isMinimized: false,
    });
  },
}));

// Helper function to convert duration string (e.g. "3:45") to seconds
function calculateDuration(duration: any): number {
  // Handle undefined, null, or falsy cases
  if (!duration) return 0;

  // Convert to string for consistent processing
  const durationStr = String(duration);

  // Handle empty string
  if (!durationStr.trim()) return 0;

  // Check if it's a time format (contains colon)
  if (durationStr.includes(":")) {
    const parts = durationStr.split(":");
    if (parts.length === 2) {
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);
      if (isNaN(minutes) || isNaN(seconds)) return 0;
      return minutes * 60 + seconds;
    }
    if (parts.length === 3) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return 0;
      return hours * 3600 + minutes * 60 + seconds;
    }
  }

  // If it's just a number as string, parse it
  const numValue = parseFloat(durationStr);
  if (!isNaN(numValue)) {
    // If the number is very large (> 10 hours), it's likely milliseconds
    if (numValue > 36000) {
      return Math.floor(numValue / 1000);
    }
    // If it's reasonably large (> 1 hour but < 10 hours), it's likely seconds
    if (numValue > 3600) {
      return numValue;
    }
    // For smaller numbers, assume seconds
    return numValue;
  }

  return 0;
}

// Helper function to format seconds to MM:SS
export function formatTime(seconds: number): string {
  if (isNaN(seconds) || !isFinite(seconds)) {
    return "0:00";
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
